import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Globe, LogIn } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Logo, AnniversaryBadge } from "./Logo";
import { SocialLinks } from "./SocialLinks";

const NAV = [
  { key: "home", to: "/", children: [{ key: "investmentModel", to: "/investment-model" }] },
  { key: "about", to: "/about" },
  { key: "projects", to: "/projects" },
  { key: "portfolio", to: "/portfolio" },
  { key: "packages", to: "/packages" },
  { key: "villa", to: "/villa-construction" },
  { key: "agriInfra", to: "/agricultural-infrastructure" },
  { key: "regions", to: "/regions" },
  { key: "media", to: "/media" },
  { key: "contact", to: "/contact" },
];

export const Navbar = () => {
  const { t, lang, setLang, languages } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 h-[76px] sm:h-[92px] transition-[background-color,box-shadow] duration-500 ${
        scrolled ? "glass-dark shadow-[0_8px_40px_rgba(0,0,0,0.35)]" : "bg-charcoal/90 backdrop-blur-md"
      }`}
      data-testid="main-header"
    >
      <div className="h-full max-w-[1720px] mx-auto px-3 sm:px-8 xl:px-3.5 2xl:px-8 flex items-center justify-between gap-2 sm:gap-4 xl:gap-3 2xl:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <Logo to="/" height={44} className="sm:hidden" testid="logo-img-mobile" linkTestid="logo-link-mobile" />
          <Logo to="/" height={scrolled ? 48 : 56} className="hidden sm:inline-flex" />
          <span className="hidden min-[390px]:block sm:block w-px h-8 sm:h-10 bg-white/25" />
          <AnniversaryBadge compact className="sm:hidden" testid="anniversary-badge-mobile" />
          <AnniversaryBadge className="hidden sm:inline-flex" />
        </div>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-2 2xl:gap-4">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.key} className="relative group">
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.key}`}
                  className={({ isActive }) =>
                    `flex items-center gap-1 text-[12px] 2xl:text-[13px] tracking-wide whitespace-nowrap transition-colors duration-300 ${
                      isActive ? "text-gold" : "text-white/85 hover:text-white"
                    }`
                  }
                >
                  {t.nav[item.key]}
                  <ChevronDown className="w-3.5 h-3.5 opacity-70 transition-transform duration-300 group-hover:rotate-180" />
                </NavLink>
                <div className="absolute start-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="min-w-[190px] glass-dark rounded-xl border border-white/10 overflow-hidden py-1.5">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.key}
                        to={child.to}
                        data-testid={`nav-${child.key}`}
                        className={({ isActive }) =>
                          `block px-4 py-2.5 text-[13px] whitespace-nowrap transition-colors ${
                            isActive ? "text-gold bg-white/5" : "text-white/85 hover:bg-white/5 hover:text-white"
                          }`
                        }
                      >
                        {t.nav[child.key]}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.key}`}
                className={({ isActive }) =>
                  `text-[12px] 2xl:text-[13px] tracking-wide whitespace-nowrap link-underline transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-white/85 hover:text-white"
                  }`
                }
              >
                {t.nav[item.key]}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 xl:gap-2 2xl:gap-3">
          <SocialLinks theme="dark" compact place="header" only={["instagram", "youtube"]} className="hidden min-[390px]:flex xl:hidden shrink-0" />
          <SocialLinks theme="dark" compact place="header-desktop" only={["instagram"]} className="hidden 2xl:flex shrink-0" />

          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              data-testid="lang-switcher"
              className="flex items-center gap-1.5 text-white/90 hover:text-gold transition-colors text-[14px] px-1.5 sm:px-2 py-1.5"
            >
              <Globe className="w-[18px] h-[18px]" />
              <span className="font-medium">{languages.find((l) => l.code === lang)?.label}</span>
              <ChevronDown className="w-4 h-4" />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute end-0 mt-2 w-36 glass-dark rounded-xl overflow-hidden border border-white/10"
                >
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      data-testid={`lang-option-${l.code}`}
                      className={`w-full text-start px-4 py-2.5 text-sm transition-colors ${
                        l.code === lang ? "text-gold bg-white/5" : "text-white/85 hover:bg-white/5"
                      }`}
                    >
                      {l.name}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/app"
            data-testid="nav-panel-login"
            aria-label={t.cta.panel}
            title={t.cta.panel}
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-white/25 text-white text-[13px] font-semibold px-3 min-[1760px]:px-4 py-2.5 hover:border-gold hover:text-gold transition-colors"
          >
            <LogIn className="w-4 h-4" />
            <span className="hidden min-[1760px]:inline">{t.cta.panel}</span>
          </Link>

          <Link
            to="/contact"
            data-testid="nav-book-cta"
            className="hidden md:inline-flex btn-shine items-center whitespace-nowrap rounded-full bg-gold text-charcoal text-[13px] font-semibold px-4 xl:px-5 py-2.5 hover:bg-gold-soft transition-colors"
          >
            <span className="hidden min-[1760px]:inline">{t.cta.book}</span>
            <span className="min-[1760px]:hidden">{t.cta.bookShort}</span>
          </Link>

          <button
            className="xl:hidden text-white p-1"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="absolute top-[76px] sm:top-[92px] inset-x-0 xl:hidden bg-charcoal overflow-hidden border-t border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {NAV.map((item) => (
                <React.Fragment key={item.key}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`mobile-nav-${item.key}`}
                    className={({ isActive }) =>
                      `py-2.5 text-[15px] border-b border-white/5 ${isActive ? "text-gold" : "text-white/85"}`
                    }
                  >
                    {t.nav[item.key]}
                  </NavLink>
                  {(item.children || []).map((child) => (
                    <NavLink
                      key={child.key}
                      to={child.to}
                      data-testid={`mobile-nav-${child.key}`}
                      className={({ isActive }) =>
                        `ps-4 py-2.5 text-[14px] border-b border-white/5 ${isActive ? "text-gold" : "text-white/70"}`
                      }
                    >
                      {t.nav[child.key]}
                    </NavLink>
                  ))}
                </React.Fragment>
              ))}
              <Link to="/contact" className="mt-4 text-center rounded-full bg-gold text-charcoal font-semibold py-3">
                {t.cta.book}
              </Link>
              <Link to="/app" data-testid="mobile-nav-panel-login"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full border border-white/25 text-white font-semibold py-3 hover:border-gold hover:text-gold transition-colors">
                <LogIn className="w-4 h-4" /> {t.cta.panel}
              </Link>
              <SocialLinks theme="dark" place="drawer" className="mt-5 justify-center" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
